/**
 * Areas: the signed-in user's scope, and the one place an area code becomes a name.
 *
 * `GET /auth/me` reports the areas whose mail the user may see: `["all"]`, a list of
 * role codes (`["dk", "de"]`), or an empty list when no area is assigned yet.
 * `GET /areas` (stage 3) describes the areas of that scope: the entity code used on a
 * case and in the queue filter (`wgg` for Germany), the label, the mailboxes, whether
 * the item catalogue is live and whether sent documents are read for it.
 *
 * The backend enforces the visibility; this module only words it for the UI. There is
 * no label table here: a name comes from `/areas`, and without it the upper-case code
 * is printed.
 */

import { useMemo } from "react";

import { type AreaInfo, type LiveUserProfile, useAreas, useMe } from "@/lib/api";
import { getAreas } from "@/lib/mock";

/** True only in the Vite dev server; statically `false` in a production build. */
const DEV_FIXTURES = import.meta.env.DEV;

/** The one sentence shown wherever a user without an area would see mail. */
export const NO_AREA_MESSAGE =
  "Your account has no area assigned yet. Ask IT to assign your area for Norican I2Q.";

/** Shown for a case or a mailbox that has no area. */
export const NO_AREA_YET = "No area yet";

/** Shown in the agreement columns for an area whose sent documents are not read. */
export const NO_REPLY_SOURCE = "Not tracked yet";

const AREA_ALL = "all";

function norm(code: unknown): string {
  return typeof code === "string" ? code.trim().toLowerCase() : "";
}

/** Usable entries of an untrusted `/areas` answer (an entry needs a code). */
export function toAreaList(raw: unknown): AreaInfo[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (entry): entry is AreaInfo =>
        entry != null && typeof entry === "object" && norm((entry as AreaInfo).code) !== "",
    )
    .map((entry) => ({ ...entry, code: norm(entry.code) }));
}

/** The area with this entity code, or (for a role code such as `de`) with this role. */
export function findArea(
  code: string | null | undefined,
  areas: readonly AreaInfo[] | null | undefined,
): AreaInfo | undefined {
  const key = norm(code);
  if (key === "" || !areas) {
    return undefined;
  }
  return (
    areas.find((area) => norm(area.code) === key) ?? areas.find((area) => norm(area.role) === key)
  );
}

/**
 * The name of an area: the label from `/areas`, else the upper-case code. Never null
 * and never empty: without a code it reads "No area yet".
 */
export function areaLabel(
  code: string | null | undefined,
  areas?: readonly AreaInfo[] | null,
): string {
  const key = norm(code);
  if (key === "" || key === "none" || key === "unknown") {
    return NO_AREA_YET;
  }
  const label = findArea(key, areas)?.label;
  return typeof label === "string" && label.trim() !== "" ? label.trim() : key.toUpperCase();
}

/** The name of an area, with "(catalogue not live yet)" when `/areas` says so. */
export function areaLabelWithState(
  code: string | null | undefined,
  areas?: readonly AreaInfo[] | null,
): string {
  const label = areaLabel(code, areas);
  return findArea(code, areas)?.live === false ? `${label} (catalogue not live yet)` : label;
}

/** False only when `/areas` says no sent documents are read for the area. */
export function hasReplySource(
  code: string | null | undefined,
  areas?: readonly AreaInfo[] | null,
): boolean {
  return findArea(code, areas)?.reply_source !== false;
}

export interface AreaLabels {
  /** The areas of the user's scope; empty until `/areas` answered (or on an older backend). */
  areas: AreaInfo[];
  label: (code: string | null | undefined) => string;
  labelWithState: (code: string | null | undefined) => string;
  hasReplySource: (code: string | null | undefined) => boolean;
}

/** The labels of the user's areas, from the cached `GET /areas`. */
export function useAreaLabels(): AreaLabels {
  const { data, isError } = useAreas();
  return useMemo(() => {
    // Local dev without a backend: the sample areas, like the other fixtures.
    const areas = toAreaList(DEV_FIXTURES && isError ? getAreas() : data);
    return {
      areas,
      label: (code) => areaLabel(code, areas),
      labelWithState: (code) => areaLabelWithState(code, areas),
      hasReplySource: (code) => hasReplySource(code, areas),
    };
  }, [data, isError]);
}

export interface MyAreas {
  /** The area codes as reported by the backend (`["all"]`, codes, or empty). */
  areas: string[];
  /** True when the user may see every mailbox. */
  all: boolean;
  /** True once the profile is loaded and it reports an empty area list. */
  none: boolean;
  /** "All areas", "Areas: Denmark, France", "No area assigned", or "" when not reported. */
  label: string;
}

/** Word a list of area codes for the user menu. */
export function areasLabel(codes: readonly string[], areas?: readonly AreaInfo[] | null): string {
  if (codes.includes(AREA_ALL)) {
    return "All areas";
  }
  if (codes.length === 0) {
    return "No area assigned";
  }
  const names = codes.map((code) => areaLabel(code, areas));
  return `${names.length === 1 ? "Area" : "Areas"}: ${names.join(", ")}`;
}

/**
 * Read the areas off a profile.
 *
 * `none` is true only when the backend reports an empty list. A profile without the
 * `areas` field (a backend that does not report areas yet) is not treated as "no area".
 */
export function areasOf(
  me: LiveUserProfile | undefined,
  infos?: readonly AreaInfo[] | null,
): MyAreas {
  const reported: unknown = me?.areas;
  const known = Array.isArray(reported);
  const areas = known ? (reported as string[]) : [];
  return {
    areas,
    all: areas.includes(AREA_ALL),
    none: known && areas.length === 0,
    label: known ? areasLabel(areas, infos) : "",
  };
}

/** The signed-in user's areas, from the cached `GET /auth/me` and `GET /areas`. */
export function useMyAreas(): MyAreas {
  const { data: me } = useMe();
  const { data: infos } = useAreas();
  return areasOf(me, toAreaList(infos));
}

/** True when the profile holds the capability. The server stays the gate. */
export function hasCapability(me: LiveUserProfile | undefined, capability: string): boolean {
  const capabilities: unknown = me?.capabilities;
  return Array.isArray(capabilities) && capabilities.includes(capability);
}

/** The work roles that do more than read the summary (`auth/roles.py`). */
const WORK_ROLES = ["manager", "admin"];

/**
 * True when the profile's only work role is `executive` (Timo, 10 Oct 2026): the
 * person reads the dashboard Summary and the monthly report and does no sales work, so
 * the queue, the cases, the rules and the other dashboard tabs are hidden. With
 * `manager` or `admin` beside it the role only adds. The server stays the gate: it
 * answers 403 to such a session on every route of the sales work.
 */
export function isExecutiveOnly(me: LiveUserProfile | undefined): boolean {
  const roles: unknown = me?.roles;
  if (!Array.isArray(roles)) {
    return false;
  }
  const held = roles.filter((role): role is string => typeof role === "string");
  return held.includes("executive") && !held.some((role) => WORK_ROLES.includes(role));
}
